'use client';

import Image from 'next/image';
import { CircledArrow } from 'assets/icons/CircledArrow';
import { ShippingBox } from 'assets/icons';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslations } from 'next-intl';
import { useCartStore } from 'components/shared/Cart';
import { RingSizes } from 'content/products/ring/productMap/constants';
import { RingProColors } from 'content/products/ring-pro';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { SequentialStepState } from 'lib/buy/sequentialSteps';
import { trackPickupEvent } from './pickupAnalytics';
import {
  BEST_BUY_PICKUP_CATALOGUE_ENABLED,
  BEST_BUY_PICKUP_ENABLED,
} from 'utils/featureFlags';
import tagIcon from 'assets/images/best-buy-pickup/tag.svg';
import infoIcon from 'assets/images/best-buy-pickup/info.svg';
import type { BestBuyPickupStore } from './api';
import {
  BB_PICKUP_GTM_EVENTS,
  BB_BUY_PAGE_VISIBLE_STORES,
  US_STANDARD_SHIPPING_USD,
  US_ZIP_RE,
} from './config';
import { formatPriceWithSymbol } from 'hooks/pricing/useFormattedPriceNew';
import { Currency } from 'utils/Currency';
import {
  ensurePickupSelectionHydrated,
  selectPickupStore,
  setPickupMode,
  usePickupSelection,
} from './pickupSelection';
import { pickupSkuFor } from './pickupSku';
import { getPickupLines } from './pickupCart';
import { usePickupCatalogueState } from './pickupCatalogue';
import { useBuyPagePickup } from './useBuyPagePickup';
import { usePickupEta } from './usePickupEta';
import { BuyPageStoresModal } from './BuyPageStoresModal';
import { StoreRowContent } from './StoreRowContent';
import styles from './BuyPagePickup.module.css';

type Props = {
  color: RingProColors | null;
  size: RingSizes | null;
  engraving: boolean;
  /** The block's state; defaults to interactive. */
  state?: SequentialStepState;
  /** The page's "Ships by <date>" line, shown on the Ship card. */
  shipsBy?: ReactNode;
  /** Takes the shopper to the size picker (sizing kit, no size, or a color
   * and size Best Buy doesn't stock). */
  onChooseSize?: () => void;
  /** Takes the shopper to the color picker (a color and size Best Buy
   * doesn't stock). */
  onChooseColor?: () => void;
  /** Root element, for the step helpers (scroll, highlight). */
  sectionRef?: RefObject<HTMLDivElement | null>;
};

/** "Delivery" step (to your door or Best Buy pickup) right after the size
 * step (Figma Pricing Page Flow V2). Always shown on the US store; the pickup
 * card is greyed out while pickup is not possible (sizing kit, no size, the
 * catalogue still loading or off, or a color and size Best Buy doesn't stock). Renders nothing unless the pickup
 * flag is on and the cart store is US. */
export const BuyPagePickup = (props: Props) => {
  // Module constant: the same branch on every render of a given build.
  if (!BEST_BUY_PICKUP_ENABLED) return null;
  return <BuyPagePickupInner {...props} />;
};

type PickupState =
  | 'kit'
  | 'checking'
  | 'off'
  | 'unavailable'
  | 'available';

/** The color's display name. Literal keys: i18n:check-keys rejects dynamic
 * ones. */
const colorName = (
  t: ReturnType<typeof useTranslations>,
  color: RingProColors,
): string => {
  switch (color) {
    case RingProColors.GOLD:
      return t('ring-pro.productSelection.GOLD');
    case RingProColors.SILVER:
      return t('ring-pro.productSelection.SILVER');
    case RingProColors.BLACK:
      return t('ring-pro.productSelection.BLACK');
    case RingProColors.TITANIUM:
      return t('ring-pro.productSelection.TITANIUM');
  }
};

/** Focus after the dialog closes: a frame later, once the block is no longer
 * inert. Falls back to the block's heading if the target went away. */
const focusAfterClose = (target: () => HTMLElement | null) => {
  requestAnimationFrame(() => {
    const node = target();
    if (node?.isConnected) {
      node.focus({ preventScroll: true });
      node.scrollIntoView({ block: 'nearest' });
      return;
    }
    document
      .querySelector<HTMLElement>('[data-testid="bb-delivery"] h5')
      ?.focus();
  });
};

const smoothScroll = (): ScrollBehavior =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

const BuyPagePickupInner = ({
  color,
  size,
  engraving,
  state = 'complete',
  shipsBy,
  onChooseSize,
  onChooseColor,
  sectionRef,
}: Props) => {
  const t = useTranslations('ring-pro');
  const cartStore = useCartStore((s) => s.store);
  // A pickup ring already in the cart: one pickup order holds one ring.
  const cartPickupStore = useCartStore((s) =>
    getPickupLines(s.cart)[0]?.storeName,
  );
  const cartHoldsPickup = useCartStore(
    (s) => getPickupLines(s.cart).length > 0,
  );
  // Re-renders when the Best Buy catalogue loads, so availability stays current.
  const catalogue = usePickupCatalogueState(cartStore === ShopifyStore.US);
  const selection = usePickupSelection();
  const groupId = useId();
  const zipId = useId();
  const shipTitleId = useId();
  const shipHintId = useId();
  const shipPriceId = useId();
  const pickupTitleId = useId();
  const pickupHintId = useId();
  const pickupPriceId = useId();
  const boxTextId = useId();
  const zipErrorId = useId();
  const zipNoteId = useId();
  const failureId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [zipTouched, setZipTouched] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // The store last picked in the dialog: announced in the live region.
  const [announcedId, setAnnouncedId] = useState<string | null>(null);
  const storesRef = useRef<HTMLDivElement>(null);
  const showAllRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    ensurePickupSelectionHydrated();
  }, []);

  const isUs = cartStore === ShopifyStore.US;
  const sku = pickupSkuFor({ store: cartStore, color, size, engraving });
  // No size yet counts as the kit: pickup needs a real ring size.
  const blockedByKit = !size || size === RingSizes.OPEN;
  const blocked = sku === null;
  // Needs `answered` too: a session saved before that flag existed would
  // otherwise show pickup checked on an unanswered step, and a tap on a
  // checked radio fires no change.
  const pickupOn =
    isUs && selection.answered && selection.mode === 'pickup' && !blocked;
  // Nothing pre-selected while pickup is possible: Delivery is a step the
  // shopper answers, and a pre-checked radio fires no change when tapped.
  // Blocked too: the shopper still answers, so the greyed pickup card and its
  // box are read before moving on. The stored pickup choice is left alone:
  // back on a size the store stocks, it returns.
  const shipOn = selection.answered && selection.mode === 'ship';
  // Off wins over everything (no kit box while pickup is off): the catalogue
  // is down, the API switched pickup off, or the catalogue flag is off (no
  // request is ever made). Then the kit, then still loading ('checking'),
  // then ready and either listing this color and size ('available') or not
  // ('unavailable').
  const pickupState: PickupState =
    !BEST_BUY_PICKUP_CATALOGUE_ENABLED ||
    catalogue.status === 'error' ||
    (catalogue.status === 'ready' && catalogue.off)
      ? 'off'
      : blockedByKit
        ? 'kit'
        : catalogue.status !== 'ready'
          ? 'checking'
          : blocked
            ? 'unavailable'
            : 'available';
  const showBox = pickupState === 'kit' || pickupState === 'unavailable';
  const pickup = useBuyPagePickup({ sku, active: pickupOn });

  const chosen =
    selection.store && selection.store.sku === sku ? selection.store : null;
  // The pickup line the rest of the page shows in place of "Ships by", on the
  // card too, so the shopper compares dates between the two cards.
  const pickupEta = usePickupEta({ store: cartStore, color, size, engraving });

  // Add to cart with pickup chosen but no store: bring the shopper here.
  const lastPrompt = useRef(selection.promptNonce);
  const promptShown = selection.promptNonce > 0 && pickupOn && !chosen;
  useEffect(() => {
    if (selection.promptNonce === lastPrompt.current) return;
    lastPrompt.current = selection.promptNonce;
    rootRef.current?.scrollIntoView({
      block: 'center',
      behavior: smoothScroll(),
    });
    zipRef.current?.focus({ preventScroll: true });
  }, [selection.promptNonce]);

  // Exposure, once per mount of the choice: buy-page conversion with the
  // pickup choice shown vs not.
  // Fires only once pickup is actually offered, so the funnel does not inflate
  // with the greyed-out state.
  const visibleToShopper = isUs && Boolean(color);
  const pickupOffered = visibleToShopper && sku !== null;
  const shownTracked = useRef(false);
  useEffect(() => {
    if (!pickupOffered || shownTracked.current) return;
    shownTracked.current = true;
    trackPickupEvent(BB_PICKUP_GTM_EVENTS.SHOWN, {
      entry: 'buy_page',
      sku_mode: 'exact',
    });
  }, [pickupOffered]);

  // Delivery re-opening after a size change does not scroll or announce (the
  // sequential steps stay quiet on backward moves), so say it here: only on a
  // blocked -> available change the shopper caused. Armed by their first tap
  // or key press, so the saved size restored on load (or the catalogue
  // landing) never announces by itself.
  const wasBlocked = useRef(blocked);
  const prevSelection = useRef({ color, size });
  const announceArmed = useRef(false);
  const [announceAvailable, setAnnounceAvailable] = useState(false);
  useEffect(() => {
    const arm = () => {
      announceArmed.current = true;
    };
    window.addEventListener('pointerdown', arm, { once: true, capture: true });
    window.addEventListener('keydown', arm, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', arm, { capture: true });
      window.removeEventListener('keydown', arm, { capture: true });
    };
  }, []);
  useEffect(() => {
    // Only a color or size change the shopper made: a catalogue landing or a
    // retry that flips blocked -> available stays quiet.
    const selectionChanged =
      prevSelection.current.color !== color ||
      prevSelection.current.size !== size;
    if (
      wasBlocked.current &&
      !blocked &&
      announceArmed.current &&
      selectionChanged
    ) {
      setAnnounceAvailable(true);
    }
    if (blocked) setAnnounceAvailable(false);
    wasBlocked.current = blocked;
    prevSelection.current = { color, size };
  }, [blocked, color, size]);

  // Esc, the close button or the scrim: back to the trigger. Stable, so the
  // dialog's focus trap does not re-engage on every render.
  const closeDialog = useCallback(() => {
    setModalOpen(false);
    focusAfterClose(() => showAllRef.current);
  }, []);

  if (!isUs) return null;

  const setRoot = (node: HTMLDivElement | null) => {
    rootRef.current = node;
    if (sectionRef) sectionRef.current = node;
  };

  const stores = pickup.stores.status === 'results' ? pickup.stores.stores : [];
  const noneHaveSize =
    stores.length > 0 && stores.every((store) => !store.allItemsAvailable);
  const listed = noneHaveSize ? [] : stores;
  const top = listed.slice(0, BB_BUY_PAGE_VISIBLE_STORES);
  // A store chosen in the dialog (or restored) past the first rows leads the
  // list, so the inline block stays at the same height.
  const chosenRow = chosen
    ? listed.find((store) => store.location_id === chosen.location_id)
    : undefined;
  const visible =
    chosenRow && !top.includes(chosenRow)
      ? [chosenRow, ...listed.slice(0, BB_BUY_PAGE_VISIBLE_STORES - 1)]
      : top;
  const hasMore = listed.length > BB_BUY_PAGE_VISIBLE_STORES;
  // Closes by itself when its list goes away (new ZIP or size, a refetch,
  // a switch to shipping, the step locking).
  const dialogVisible =
    modalOpen && pickupOn && state !== 'locked' && hasMore;

  // The dialog went away with its list: drop the flag too, so it does not
  // reopen by itself when a list comes back.
  if (modalOpen && !dialogVisible) setModalOpen(false);

  const pick = (store: BestBuyPickupStore) => {
    if (!sku || !pickup.validZip || !store.allItemsAvailable) return;
    if (chosen?.location_id === store.location_id) return;
    setAnnouncedId(null);
    selectPickupStore({
      location_id: store.location_id,
      name: store.name,
      zip: pickup.validZip,
      sku,
      pickup_date: store.pickup_date,
    });
  };
  const pickFromDialog = (store: BestBuyPickupStore) => {
    pick(store);
    // After pick(), which clears it for inline picks.
    setAnnouncedId(store.location_id);
    setModalOpen(false);
    focusAfterClose(
      () =>
        storesRef.current?.querySelector<HTMLInputElement>(
          `[data-testid="bb-store-${store.location_id}"]`,
        ) ?? null,
    );
  };

  const shipFromDialog = () => {
    setModalOpen(false);
    setPickupMode('ship');
    focusAfterClose(() =>
      rootRef.current?.querySelector<HTMLInputElement>(
        '[data-testid="bb-delivery-ship"]',
      ) ?? null,
    );
  };

  const zipInvalid = zipTouched && pickup.zip.length > 0 && !pickup.validZip;
  const failure =
    pickup.stores.status === 'error' ? pickup.stores.failure : null;
  const failureMessage = !failure
    ? ''
    : failure.reason === 'invalid_zip'
      ? failure.message || t('ring-pro.bestBuyPickup.invalidZip')
      : failure.reason === 'rate_limited'
        ? failure.message || t('ring-pro.bestBuyPickup.rateLimited')
        : t('ring-pro.bestBuyPickup.error');

  const blurOnTouch = () => {
    if (window.matchMedia('(pointer: coarse)').matches) {
      zipRef.current?.blur();
    }
  };

  const tryDifferentZip = () => {
    setZipTouched(false);
    setModalOpen(false);
    pickup.setZip('');
    zipRef.current?.focus();
  };

  return (
    <div
      className={styles.root}
      ref={setRoot}
      data-state={state}
      data-pickup={pickupState}
      inert={state === 'locked' || dialogVisible}
      data-testid="bb-delivery"
    >
      {/* section-row-title: the step helpers (scroll, flash, announce)
          anchor on it, as on the buy page's other sections. */}
      <div className="section-row-title">
        <h5 className={styles.title} id={groupId} tabIndex={-1}>
          {t('ring-pro.bestBuyPickup.title')}
        </h5>
      </div>
      <div
        className={styles.options}
        role="radiogroup"
        aria-labelledby={groupId}
      >
        <label
          className={`${styles.option} ${shipOn ? styles.optionActive : ''}`}
        >
          <input
            className={styles.input}
            type="radio"
            name={`${groupId}-delivery`}
            checked={shipOn}
            onChange={() => setPickupMode('ship')}
            aria-labelledby={shipTitleId}
            aria-describedby={
              shipsBy ? `${shipHintId} ${shipPriceId}` : shipPriceId
            }
            data-testid="bb-delivery-ship"
          />
          <span className={styles.optionTop}>
            <ShippingBox className={styles.icon} />
            <span className={styles.price} id={shipPriceId}>
              {t('ring-pro.bestBuyPickup.shipPrice', {
                price: formatPriceWithSymbol(
                  US_STANDARD_SHIPPING_USD,
                  Currency.usd,
                ),
              })}
            </span>
          </span>
          <span className={styles.optionLabel} id={shipTitleId}>
            {t('ring-pro.bestBuyPickup.shipLabel')}
          </span>
          {shipsBy ? (
            <span className={styles.optionHint} id={shipHintId}>
              {shipsBy}
            </span>
          ) : null}
        </label>
        <label
          className={`${styles.option} ${pickupOn ? styles.optionActive : ''} ${
            blocked ? styles.optionDisabled : ''
          }`}
        >
          <input
            className={styles.input}
            type="radio"
            name={`${groupId}-delivery`}
            checked={pickupOn}
            aria-disabled={blocked ? 'true' : undefined}
            onChange={() => {
              // aria-disabled keeps the card focusable so its reason is
              // reachable; a blocked card must never change the selection.
              if (blocked) return;
              setPickupMode('pickup');
            }}
            onClick={(event) => {
              if (blocked) event.preventDefault();
            }}
            aria-labelledby={pickupTitleId}
            aria-describedby={
              !blocked
                ? `${pickupHintId} ${pickupPriceId}`
                : showBox
                  ? `${pickupHintId} ${boxTextId}`
                  : pickupHintId
            }
            data-testid="bb-delivery-pickup"
          />
          <span className={styles.optionTop}>
            <Image src={tagIcon} alt="" className={styles.icon} />
            {!blocked ? (
              <span className={styles.price} id={pickupPriceId}>
                {t('ring-pro.bestBuyPickup.pickupFree')}
              </span>
            ) : null}
          </span>
          <span className={styles.optionLabel} id={pickupTitleId}>
            {t('ring-pro.bestBuyPickup.pickupLabel')}
          </span>
          <span className={styles.optionHint} id={pickupHintId}>
            {pickupState === 'kit'
              ? t('ring-pro.bestBuyPickup.pickupBlockedSizingKit')
              : pickupState === 'checking'
                ? t('ring-pro.bestBuyPickup.pickupChecking')
                : pickupState === 'off'
                  ? t('ring-pro.bestBuyPickup.pickupOff')
                  : pickupState === 'unavailable'
                    ? t('ring-pro.bestBuyPickup.pickupBlockedSize')
                    : (pickupEta ?? t('ring-pro.bestBuyPickup.pickupHint'))}
          </span>
        </label>
      </div>

      {cartHoldsPickup ? (
        <div className={styles.info} data-testid="bb-cart-has-pickup-note">
          <Image src={infoIcon} alt="" className={styles.icon} />
          <div className={styles.infoBody}>
            <p className={styles.infoText}>
              {t('ring-pro.bestBuyPickup.cartHasPickupNote', {
                store: cartPickupStore ?? '',
              })}
            </p>
          </div>
        </div>
      ) : null}

      {showBox ? (
        <div
          className={styles.info}
          data-testid={blockedByKit ? 'bb-kit-info' : 'bb-unavailable'}
          role={blockedByKit ? undefined : 'status'}
        >
          <Image src={infoIcon} alt="" className={styles.icon} />
          <div className={styles.infoBody}>
            <p className={styles.infoText} id={boxTextId}>
              {blockedByKit
                ? t('ring-pro.bestBuyPickup.kitInfo')
                : t('ring-pro.bestBuyPickup.pickupUnavailableBox', {
                    color: color ? colorName(t, color) : '',
                    size: size ?? '',
                  })}
            </p>
            <div className={styles.infoActions}>
              {blockedByKit ? (
                onChooseSize ? (
                  <button
                    type="button"
                    className={styles.link}
                    onClick={onChooseSize}
                    data-testid="bb-choose-size"
                  >
                    {t('ring-pro.bestBuyPickup.chooseSize')}
                  </button>
                ) : null
              ) : (
                <>
                  {onChooseColor ? (
                    <button
                      type="button"
                      className={styles.link}
                      onClick={onChooseColor}
                      data-testid="bb-change-color"
                    >
                      {t('ring-pro.bestBuyPickup.changeColor')}
                    </button>
                  ) : null}
                  {onChooseSize ? (
                    <button
                      type="button"
                      className={styles.link}
                      onClick={onChooseSize}
                      data-testid="bb-change-size"
                    >
                      {t('ring-pro.bestBuyPickup.changeSize')}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <span
        className={styles.visuallyHidden}
        aria-live="polite"
        data-testid="bb-pickup-available-announce"
      >
        {announceAvailable
          ? t('ring-pro.bestBuyPickup.pickupAvailableAnnounce')
          : ''}
      </span>


      {pickupOn ? (
        <div className={styles.panel}>
          <div className={styles.field}>
            <label className={styles.zipLabel} htmlFor={zipId}>
              {t('ring-pro.bestBuyPickup.zipLabel')}
            </label>
            <input
              id={zipId}
              ref={zipRef}
              className={styles.zip}
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              enterKeyHint="search"
              maxLength={5}
              placeholder={t('ring-pro.bestBuyPickup.zipPlaceholder')}
              value={pickup.zip}
              aria-invalid={zipInvalid || failure?.reason === 'invalid_zip'}
              aria-describedby={
                zipInvalid
                  ? zipErrorId
                  : failure
                    ? failureId
                    : !pickup.validZip
                      ? zipNoteId
                      : undefined
              }
              onChange={(event) => {
                setZipTouched(false);
                setModalOpen(false);
                pickup.setZip(event.target.value);
                // The 5th digit starts the lookup: close the on-screen
                // keyboard so the stores show. Touch only, so desktop
                // keyboard users keep focus in the field.
                if (US_ZIP_RE.test(event.target.value)) blurOnTouch();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && pickup.validZip) blurOnTouch();
              }}
              onBlur={() => setZipTouched(true)}
              data-testid="bb-zip"
            />
            {zipInvalid ? (
              <p className={styles.error} role="alert" id={zipErrorId}>
                {t('ring-pro.bestBuyPickup.invalidZip')}
              </p>
            ) : null}
            {!pickup.validZip && !zipInvalid ? (
              <p className={styles.note} id={zipNoteId}>
                {t('ring-pro.bestBuyPickup.enterZip')}
              </p>
            ) : null}
          </div>

          {pickup.stores.status === 'loading' ? (
            <p
              className={styles.note}
              data-testid="bb-loading"
              aria-hidden="true"
            >
              {t('ring-pro.bestBuyPickup.loading', { zip: pickup.zip })}
            </p>
          ) : null}

          {/* Announces the lookup: that it is running, then the count, or
              that no store has the size. The loading copy is repeated here
              out of view so the note above keeps its own slot. */}
          <div aria-live="polite" className={styles.live}>
            {pickup.stores.status === 'loading' ? (
              <span className={styles.visuallyHidden}>
                {t('ring-pro.bestBuyPickup.loading', { zip: pickup.zip })}
              </span>
            ) : null}
            {pickup.stores.status === 'results' && !noneHaveSize ? (
              <p className={styles.count}>
                {stores.length === 1
                  ? t('ring-pro.bestBuyPickup.resultsOne', { zip: pickup.zip })
                  : t('ring-pro.bestBuyPickup.resultsMany', {
                      count: stores.length,
                      zip: pickup.zip,
                    })}
              </p>
            ) : null}
            {chosen && announcedId === chosen.location_id ? (
              <p className={styles.visuallyHidden}>
                {t('ring-pro.bestBuyPickup.modalSelected', {
                  name: chosen.name,
                })}
              </p>
            ) : null}
            {noneHaveSize ? (
              <p className={styles.message} data-testid="bb-none-have-size">
                {stores.length === 1
                  ? t('ring-pro.bestBuyPickup.noneHaveSizeOne', {
                      zip: pickup.zip,
                    })
                  : t('ring-pro.bestBuyPickup.noneHaveSize', {
                      count: stores.length,
                      zip: pickup.zip,
                    })}
              </p>
            ) : null}
          </div>
          {noneHaveSize ? (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.link}
                onClick={tryDifferentZip}
                data-testid="bb-try-different-zip"
              >
                {t('ring-pro.bestBuyPickup.tryDifferentZip')}
              </button>
              <button
                type="button"
                className={styles.link}
                onClick={() => setPickupMode('ship')}
                data-testid="bb-ship-instead-none"
              >
                {t('ring-pro.bestBuyPickup.shipInstead')}
              </button>
            </div>
          ) : null}

          {pickup.stores.status === 'results' &&
          pickup.stores.environment === 'sandbox' ? (
            <p className={styles.note} data-testid="bb-sandbox-note">
              {t('ring-pro.bestBuyPickup.sandboxNote')}
            </p>
          ) : null}

          {pickup.stores.status === 'unavailable' ? (
            <div className={styles.message} data-testid="bb-not-offered">
              <p>
                {t('ring-pro.bestBuyPickup.notOffered', { zip: pickup.zip })}
              </p>
              <button
                type="button"
                className={styles.link}
                onClick={() => setPickupMode('ship')}
                data-testid="bb-ship-instead"
              >
                {t('ring-pro.bestBuyPickup.shipInstead')}
              </button>
            </div>
          ) : null}

          {failure ? (
            <div
              className={styles.message}
              role="alert"
              id={failureId}
              data-testid="bb-error"
            >
              <p>{failureMessage}</p>
              {failure.reason !== 'invalid_zip' ? (
                <button
                  type="button"
                  className={styles.link}
                  onClick={pickup.retry}
                >
                  {t('ring-pro.bestBuyPickup.tryAgain')}
                </button>
              ) : null}
            </div>
          ) : null}

          {visible.length > 0 ? (
            <div
              ref={storesRef}
              className={styles.stores}
              role="radiogroup"
              aria-label={t('ring-pro.bestBuyPickup.storesLabel')}
            >
              {visible.map((store) => {
                const selectable = store.allItemsAvailable;
                const isChosen = chosen?.location_id === store.location_id;
                return (
                  <label
                    key={store.location_id}
                    className={`${styles.store} ${
                      isChosen ? styles.storeActive : ''
                    } ${selectable ? '' : styles.storeDisabled}`}
                  >
                    <input
                      className={styles.input}
                      type="radio"
                      name={`${groupId}-store`}
                      checked={isChosen}
                      disabled={!selectable}
                      onChange={() => pick(store)}
                      data-testid={`bb-store-${store.location_id}`}
                    />
                    <StoreRowContent store={store} sku={sku} />
                  </label>
                );
              })}
            </div>
          ) : null}

          {hasMore ? (
            <button
              ref={showAllRef}
              type="button"
              className={styles.showAll}
              onClick={() => setModalOpen(true)}
              aria-haspopup="dialog"
              data-testid="bb-show-all"
            >
              {t('ring-pro.bestBuyPickup.showMore', { count: listed.length })}
              <CircledArrow />
            </button>
          ) : null}

          {promptShown && !noneHaveSize ? (
            <p className={styles.error} role="alert" data-testid="bb-prompt">
              {t('ring-pro.bestBuyPickup.chooseStorePrompt')}
            </p>
          ) : null}
        </div>
      ) : null}
      {dialogVisible ? (
        <BuyPageStoresModal
          stores={listed}
          chosenId={chosen?.location_id ?? null}
          zip={pickup.validZip ?? pickup.zip}
          sku={sku}
          onPick={pickFromDialog}
          onShip={shipFromDialog}
          onClose={closeDialog}
        />
      ) : null}
    </div>
  );
};
